import { Api } from 'laravel-request';

import { FINANCE_GROUP_RELATIONS, MULTIPLE_MANAGERS } from '#app/api/clients/legacy';

import type { LaravelQueryBuilder } from '../types/laravelRequest';

const CLIENT_ID_WHERE_IN_CHUNK_SIZE = 500;

/**
 * id клиентов из ответа client/index — для узкого whereIn на crm-client-services.
 */
export const extractFunnelScopedClientIdsArray = (clientsData: unknown): number[] => {
    if (!Array.isArray(clientsData)) {
        return [];
    }

    const scopedClientIdsSet = new Set<number>();
    clientsData.forEach((clientEntry) => {
        if (!clientEntry || typeof clientEntry !== 'object') {
            return;
        }
        const rawClientId = (clientEntry as { id?: unknown }).id;
        const normalizedClientId =
            typeof rawClientId === 'number'
                ? rawClientId
                : parseInt(String(rawClientId ?? ''), 10);
        if (Number.isFinite(normalizedClientId) && normalizedClientId > 0) {
            scopedClientIdsSet.add(normalizedClientId);
        }
    });

    return Array.from(scopedClientIdsSet);
};

const appendClientIdWhereInChunks = (
    query: LaravelQueryBuilder,
    scopedClientIdsArray: number[]
): LaravelQueryBuilder => {
    if (scopedClientIdsArray.length === 0) {
        return query;
    }

    if (scopedClientIdsArray.length <= CLIENT_ID_WHERE_IN_CHUNK_SIZE) {
        return query.whereIn('client_id', scopedClientIdsArray);
    }

    return query.where((nestedQuery: LaravelQueryBuilder) => {
        for (
            let chunkStartIndex = 0;
            chunkStartIndex < scopedClientIdsArray.length;
            chunkStartIndex += CLIENT_ID_WHERE_IN_CHUNK_SIZE
        ) {
            const clientIdsChunk = scopedClientIdsArray.slice(
                chunkStartIndex,
                chunkStartIndex + CLIENT_ID_WHERE_IN_CHUNK_SIZE
            );
            if (chunkStartIndex === 0) {
                nestedQuery.whereIn('client_id', clientIdsChunk);
            } else if (nestedQuery.orWhereIn) {
                nestedQuery.orWhereIn('client_id', clientIdsChunk);
            }
        }
        return nestedQuery;
    });
};

/**
 * Услуги для воронки.
 * `with[]` client/index — только подсказки видимости на бэке (eager-load не нужен).
 * `scopedClientIdsArray` — узкий whereIn по уже загруженным клиентам (без paginate all по всей FG).
 */
export const buildFunnelCrmClientServicesQuery = (
    withFinanceGroup: boolean,
    scopedClientIdsArray?: number[]
) => {
    let query = Api.get('crm-client-services', 'index')
        .with(...MULTIPLE_MANAGERS)
        .with('service', (nestedQuery: LaravelQueryBuilder) => nestedQuery.where('is_archived', 0))
        .orderBy('created_at');

    if (withFinanceGroup) {
        query = query.with(...FINANCE_GROUP_RELATIONS);
    }

    if (scopedClientIdsArray && scopedClientIdsArray.length > 0) {
        query = appendClientIdWhereInChunks(query, scopedClientIdsArray);
    }

    return query;
};
